import type { CancelTokenSource } from 'axios';
import axios from 'axios';
import type {
  GetState,
  ThunkAction,
  ThunkExtras,
  AppAction,
  AppState,
  APIError,
  Dispatch,
} from '../../types/app.types';
import type { V2Pharmacy, V2Provider } from '../../types/api-v2.types';
import type { SearchResponse } from '../../types/api-v0.types';
import type { Allergy, MedicalCondition, OtherMedication } from './delivery-module.types';
import { setDefaultView } from './header-module';
import type { SearchView } from './header-module';
import { allergySearch, otherMedicationSearch, medConditionSearch, providerSearch } from './delivery-module';
import { revertHeader } from './header-module';
import { pharmacySearch } from './pharmacy-module';
import { getDrug } from './drugs-module';
import type { Drug } from './drugs.types';

export type SearchResult = {
  medNameId: string;
  name: string;
  alternateName: string;
  slug: string;
};
export type SearchState = {
  readonly term: string | null | undefined;
  readonly results: { [term in string | null | undefined]?: Array<SearchResult> };
  focused: boolean;
  recentlyViewedMeds: Array<Drug> | null | undefined;
};
export const initialState: SearchState = {
  term: null,
  results: {},
  // null here is important, we are using it to distinguish between
  // dirty and clean state. when focus is false we know its a result of user interaction
  // when it is null it means this was not set yet
  focused: null,
  recentlyViewedMeds: [],
};
export type Med = {
  name: string;
  nameId: string;
  slug: string;
};
export type Search = {
  type: 'search/SEARCH_REQUEST';
  term: string;
};
export type ClearSearch = {
  type: 'search/CLEAR_SEARCH';
};
export type SearchSuccess = {
  type: 'search/SEARCH_SUCCESS';
  results: Array<SearchResult>;
  term: string;
};
export type SearchError = {
  type: 'search/SEARCH_ERROR';
} & APIError;
export type RecentlyViewedMeds = {
  type: 'search/GET_RECENTLY_VIEWED_MED';
  data: Array<Drug>;
};
type SelectedMed = {
  searchView: 'MEDICATION_SEARCH';
  result: Med;
};
type SelectedPharmacy = {
  searchView: 'PHARMACY_SEARCH';
  result: V2Pharmacy;
};
type SelectedProvider = {
  searchView: 'PROVIDER_SEARCH';
  result: V2Provider;
};
type SelectedAllergy = {
  searchView: 'ALLERGY_SEARCH';
  result: Allergy;
};
type SelectedOtherMedication = {
  searchView: 'OTHER_MEDICATION_SEARCH';
  result: OtherMedication;
};
type SelectedMedicalCondition = {
  searchView: 'MEDICAL_CONDITION_SEARCH';
  result: MedicalCondition;
};
export type SelectedSearchItem =
  | SelectedMed
  | SelectedPharmacy
  | SelectedProvider
  | SelectedAllergy
  | SelectedOtherMedication
  | SelectedMedicalCondition;
export type SelectResult = {
  type: 'search/SELECT_RESULT';
  data: SelectedSearchItem;
};
export type SearchAction = Search | SearchSuccess | SearchError | SelectResult | ClearSearch | RecentlyViewedMeds;

function clearSearch(): ClearSearch {
  return {
    type: 'search/CLEAR_SEARCH',
  };
}

function searchError(error: APIError): SearchError {
  return {
    type: 'search/SEARCH_ERROR',
    ...error,
  };
}

function searchSuccess(results: Array<SearchResult>, term: string): SearchSuccess {
  return {
    type: 'search/SEARCH_SUCCESS',
    results,
    term,
  };
}

let medSearchCanceller: CancelTokenSource;

export function medicationSearch(term: string): ThunkAction<Promise<any>> {
  return function (dispatch: Dispatch, getState: GetState, { blinkAPIClient }: ThunkExtras): Promise<any> {
    // The backend will only send a response for request longer than 2 characters
    if (term.length < 2) {
      return Promise.resolve(getState().search.term !== '' ? dispatch(clearSearch()) : null);
    }

    if (medSearchCanceller) {
      medSearchCanceller.cancel(); // only handle one in-flight request at a time
    }

    medSearchCanceller = axios.CancelToken.source();

    const _term = term.toLowerCase().trim();

    //  grab results from cache if found
    const { results } = getState().search;

    if (results[_term]) {
      return Promise.resolve(dispatch(searchSuccess(results[_term], _term)));
    }

    return blinkAPIClient
      .get('/medications?name=' + _term, {
        cancelToken: medSearchCanceller.token,
      })
      .then((res) => res.data)
      .then((json: SearchResponse) => {
        const searchResults = json.results.map((r) => ({
          name: r.name,
          alternateName: r.alternateName,
          medNameId: r.medNameId,
          slug: r.slug,
        }));
        return dispatch(searchSuccess(searchResults, _term));
      })
      .catch((error: APIError) => {
        if (!error.isCancel) {
          dispatch(searchError(error));
        }
      });
  };
}

export function loadRecentlyViewedMeds(): RecentlyViewedMeds {
  const meds = localStorage.getItem('recentlyViewedMeds') || '';
  let data;

  try {
    data = JSON.parse(meds);
  } catch (error) {
    data = [];
  }

  return {
    type: 'search/GET_RECENTLY_VIEWED_MED',
    data,
  };
}

export function saveRecentlyViewedMeds(): ThunkAction<RecentlyViewedMeds> {
  return function (dispatch: Dispatch, getState: GetState): RecentlyViewedMeds {
    const state = getState().drugs;
    const med = getDrug(state);

    try {
      if (!localStorage.getItem('recentlyViewedMeds') && med) {
        localStorage.setItem('recentlyViewedMeds', JSON.stringify([med]));
      } else {
        const storage = localStorage.getItem('recentlyViewedMeds');
        const dedupedStorage = med && storage && JSON.parse(storage).filter((item) => item.slug !== med.slug);
        const recentlyViewedMeds = dedupedStorage && [med, ...dedupedStorage].slice(0, 5);

        if (recentlyViewedMeds) {
          localStorage.setItem('recentlyViewedMeds', JSON.stringify(recentlyViewedMeds));
        }
      }

      return dispatch(loadRecentlyViewedMeds());
    } catch (e) {
      console.error(e);
    }
  };
}

export function search(
  term: string,
  searchView: SearchView | null | undefined = 'MEDICATION_SEARCH',
  additionalParams: Record<string, any> | null | undefined = {}
) {
  switch (searchView) {
    case 'MEDICATION_SEARCH':
      return medicationSearch(term);

    case 'PHARMACY_SEARCH':
      return pharmacySearch({
        search: term,
        ...additionalParams,
      });

    case 'PROVIDER_SEARCH':
      return providerSearch(term, additionalParams);

    case 'ALLERGY_SEARCH':
      return allergySearch(term);

    case 'OTHER_MEDICATION_SEARCH':
      return otherMedicationSearch(term);

    case 'MEDICAL_CONDITION_SEARCH':
      return medConditionSearch(term);

    default:
      return medicationSearch(term);
  }
}

export function selectResult(selectedItem: SelectedSearchItem): ThunkAction<SelectResult> {
  return function (dispatch: Dispatch, getState: GetState, { history }: ThunkExtras): SelectResult {
    if (selectedItem.searchView === 'MEDICATION_SEARCH') {
      const med = selectedItem.result;
      history.push(`/${med.slug}`, {
        medNameId: med.nameId,
      });
      dispatch(setDefaultView());
    } else {
      dispatch(revertHeader());
    }

    return dispatch({
      type: 'search/SELECT_RESULT',
      data: selectedItem,
    });
  };
}

export default function searchReducer(state: SearchState = initialState, action: AppAction): SearchState {
  switch (action.type) {
    case 'search/CLEAR_SEARCH': {
      return { ...state, term: '' };
    }

    case 'header/SET_VIEW': {
      return action.view === 'MEDICATION_SEARCH' ? { ...state, term: '' } : state;
    }

    case 'search/SEARCH_SUCCESS': {
      // if we already cached this just change the term
      // the selector will use this as the pointer to the cached results
      if (state.results[action.term] || action.term === '') {
        return { ...state, term: action.term };
      }

      const newResults = { ...state.results, [action.term]: action.results };
      return { ...state, results: newResults, term: action.term };
    }

    case 'search/SELECT_RESULT':
      return { ...state, focused: false, term: '' };

    case 'search/GET_RECENTLY_VIEWED_MED':
      return { ...state, recentlyViewedMeds: action.data };

    default:
      return state;
  }
}
type GetResults = (arg0: Record<string, any>) => Array<SearchResult>;
export const getResults: GetResults = (state: AppState) => {
  const { results, term } = state.search;
  return results[term] || [];
};
